import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  // loadSimulationResponse,
  // nudgeSimulationTime,
  pausePlayback,
  // restartSimulation,
  resumePlayback,
  // seekSimulationTime,
  // startSimulation,
  stopSimulation,
} from '../../features/simulation/simulationSlice';
// import { setShowSimulationTrackNodes } from '../../features/ui/uiSlice';
import { patchProjectMetadata, updateElement } from '../../features/layout/layoutSlice';
import { setOutsideStockerExclusive } from '../../features/layout/setOutsideStockerExclusive';
// import { clearSelection } from '../../features/selection/selectionSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { selectExportableEquipmentInstanceMap } from '../../features/equipment/equipmentSelectors';
import { updateInstance } from '../../features/equipment/equipmentSlice';
import { normalizeEquipmentProcessType } from '../../features/equipment/equipmentConstants';
// import { runAmhsSimulation } from '../../utils/amhsSimulationApi';
import {
  // AmhsRunApiError,
  // formatAmhsRunError,
  type AmhsFormattedRunError,
  type AmhsFormattedValidationIssue,
} from '../../utils/formatAmhsRunError';
// import { buildAmhsRunRequest } from '../../features/simulation/buildAmhsRunRequest';
import {
  DEFAULT_AMHS_LIVE_STREAM_TUNING,
  buildAmhsStreamRequest,
  fingerprintAmhsStreamRequest,
  resolveAmhsFabId,
  resolveAmhsStreamOccupancyId,
  resolveJunctionWaitSec,
  resolveSavedCollisionAvoidance,
} from '../../features/simulation/buildAmhsStreamRequest';
import { useAmhsLiveStream } from '../../hooks/useAmhsLiveStream';
import { collectAmhsRunReadiness } from '../../features/simulation/validateAmhsRunPayload';
import {
  AMHS_STREAM_DISPATCH_LABELS,
  dispatchPolicyFromRequest,
  resolveStreamDispatchPolicy,
} from '../../features/simulation/amhsStreamDispatch';
import LiveDispatchReportPanel from './LiveDispatchReportPanel';
// import {
//   buildBrowserKineticFromFinalSimulation,
//   extractTransportRequests,
// } from '../../utils/adaptAmhsSimulationResponse';
// import { nextTravelMotionTime, phaseChipColors } from '../../utils/simulationPhaseUi';
import TransportRequestsPanel from './TransportRequestsPanel';
import SelectedOhtPanel from './SelectedOhtPanel';
import ProcessFlowConfigModal from './ProcessFlowConfigModal';
import LiveInventoryPanel from './LiveInventoryPanel';
import ToolInventoryPanel from './ToolInventoryPanel';
import ReviewSimulationConfigModal from './ReviewSimulationConfigModal';
import LabelsTogglePopup from '../common/LabelsTogglePopup';
import type { AmhsLiveStreamTuning, AmhsStreamRunRequest } from '../../types/amhsStream';
import { readProcessFlow } from '../../features/simulation/processFlow';

/** Step size for rewind/forward: ~1% of duration, clamped to a usable range. */
// function timelineStepSeconds(durationS: number): number {
//   if (durationS <= 0) return 1;
//   return Math.max(1, Math.min(60, Math.round(durationS * 0.01)));
// }

const formatSeconds = (seconds: number): string => `${seconds.toFixed(1)}s`;

type SimulationDetailTab = 'overview' | 'oht' | 'stocker' | 'tool';

const DETAIL_TABS: Array<{ id: SimulationDetailTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'oht', label: 'OHT' },
  { id: 'stocker', label: 'Stocker' },
  { id: 'tool', label: 'Tool' },
];

const SimulationPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState(true);
  // const [isRunningRequest, setIsRunningRequest] = useState(false);
  const [runError] = useState<AmhsFormattedRunError | null>(null);
  const [processFlowModalOpen, setProcessFlowModalOpen] = useState(false);
  const [errorDetailsModalOpen, setErrorDetailsModalOpen] = useState(false);
  const [reviewConfigModalOpen, setReviewConfigModalOpen] = useState(false);
  const [detailTab, setDetailTab] = useState<SimulationDetailTab>('overview');
  const [reviewedStreamRequest, setReviewedStreamRequest] = useState<AmhsStreamRunRequest | null>(
    null
  );
  const [liveSimSpeed, setLiveSimSpeed] = useState<number | null>(null);
  /** Transport flow is intentionally omitted from all simulation requests. */
  const includeTransportFlow = false;
  const simulation = useAppSelector((state) => state.simulation);
  const elements = useAppSelector((state) => state.layout.elements);
  const trackNodeAnnotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const projectMetadata = useAppSelector((state) => state.layout.projectMetadata);
  const simulationProjectMetadata = useMemo(() => {
    const inventory =
      projectMetadata && typeof projectMetadata.inventory === 'object' && projectMetadata.inventory
        ? (projectMetadata.inventory as Record<string, unknown>)
        : {};
    return {
      ...(projectMetadata ?? {}),
      inventory: {
        ...inventory,
        enabled: true,
        requireInventoryBeforeProcess: true,
        showBadges: false,
      },
    };
  }, [projectMetadata]);
  const savedLiveStream = useMemo(
    () =>
      projectMetadata &&
      typeof projectMetadata.liveStream === 'object' &&
      projectMetadata.liveStream
        ? (projectMetadata.liveStream as Partial<AmhsLiveStreamTuning>)
        : null,
    [projectMetadata]
  );

  const savedJunctionWaitSec = useMemo(
    () => resolveJunctionWaitSec(projectMetadata),
    [projectMetadata]
  );
  const savedCollisionAvoidance = useMemo(
    () => resolveSavedCollisionAvoidance(projectMetadata),
    [projectMetadata]
  );

  const effectiveLiveSimSpeed =
    typeof liveSimSpeed === 'number'
      ? liveSimSpeed
      : typeof savedLiveStream?.simSpeed === 'number' && savedLiveStream.simSpeed > 0
        ? savedLiveStream.simSpeed
        : DEFAULT_AMHS_LIVE_STREAM_TUNING.simSpeed;

  /** Single UI value: trip count + default hold capacity. Defaults to 1. */
  const foupValue = useMemo(() => {
    const flow =
      projectMetadata &&
      typeof projectMetadata.transportFlow === 'object' &&
      projectMetadata.transportFlow
        ? (projectMetadata.transportFlow as Record<string, unknown>)
        : null;
    const inventory =
      projectMetadata && typeof projectMetadata.inventory === 'object' && projectMetadata.inventory
        ? (projectMetadata.inventory as Record<string, unknown>)
        : null;
    const fromCount = Number(flow?.foupCount);
    if (Number.isFinite(fromCount) && fromCount > 0) return Math.floor(fromCount);
    const fromCap = Number(inventory?.foupCapacity);
    if (Number.isFinite(fromCap) && fromCap > 0) return Math.floor(fromCap);
    return 1;
  }, [projectMetadata]);

  // Inventory checks are always enabled; badges remain hidden.
  useEffect(() => {
    const inventory =
      projectMetadata && typeof projectMetadata.inventory === 'object' && projectMetadata.inventory
        ? (projectMetadata.inventory as Record<string, unknown>)
        : {};
    if (
      inventory.enabled === true &&
      inventory.requireInventoryBeforeProcess === true &&
      inventory.showBadges === false
    ) {
      return;
    }
    dispatch(
      patchProjectMetadata({
        inventory: {
          ...inventory,
          enabled: true,
          requireInventoryBeforeProcess: true,
          showBadges: false,
        },
      })
    );
  }, [dispatch, projectMetadata]);

  const setFoupValue = (next: number) => {
    if (!Number.isFinite(next) || next <= 0) return;
    const value = Math.floor(next);
    const prevInventory =
      projectMetadata && typeof projectMetadata.inventory === 'object' && projectMetadata.inventory
        ? (projectMetadata.inventory as Record<string, unknown>)
        : {};
    const prevFlow =
      projectMetadata &&
      typeof projectMetadata.transportFlow === 'object' &&
      projectMetadata.transportFlow
        ? (projectMetadata.transportFlow as Record<string, unknown>)
        : {};
    dispatch(
      patchProjectMetadata({
        transportFlow: {
          ...prevFlow,
          foupCount: value,
        },
        inventory: {
          ...prevInventory,
          foupCapacity: value,
          showBadges: false,
        },
      })
    );
  };

  // Only overlays visible on the canvas are simulated; overlays on hidden DXF
  // layers are destroyed on regeneration and never reach the run payload.
  const equipmentInstances = useAppSelector(selectExportableEquipmentInstanceMap);
  const allEquipmentInstances = useAppSelector((state) => state.equipment.instances);
  const equipmentTemplates = useAppSelector((state) => state.equipment.templates);
  const floorPlanId = useAppSelector((state) => state.floorplan.floorPlanId);
  const floorPlanName = useAppSelector((state) => state.floorplan.name);
  const userId = useAppSelector((state) => state.auth.userId);

  // const apiLegs = simulation.browserKinetic?.apiLegs ?? [];
  const layoutFabId = resolveAmhsFabId(floorPlanId, floorPlanName);
  const fabId = resolveAmhsStreamOccupancyId(userId, layoutFabId);
  const streamRequest = useMemo(() => {
    try {
      return buildAmhsStreamRequest({
        elements,
        equipmentInstances,
        equipmentTemplates,
        floorPlanId,
        floorPlanName,
        trackNodeAnnotations,
        projectMetadata: simulationProjectMetadata,
        includeTransportFlow,
        simSpeed: effectiveLiveSimSpeed,
        liveStream: savedLiveStream ?? undefined,
        ...(savedJunctionWaitSec != null ? { junctionWaitSec: savedJunctionWaitSec } : {}),
        ...(savedCollisionAvoidance ? { collisionAvoidance: savedCollisionAvoidance } : {}),
      });
    } catch {
      return null;
    }
  }, [
    elements,
    equipmentInstances,
    equipmentTemplates,
    floorPlanId,
    floorPlanName,
    includeTransportFlow,
    effectiveLiveSimSpeed,
    savedJunctionWaitSec,
    savedCollisionAvoidance,
    savedLiveStream,
    simulationProjectMetadata,
    trackNodeAnnotations,
  ]);
  const effectiveStreamRequest = reviewedStreamRequest ?? streamRequest;
  const activeDispatchPolicy = useMemo(() => {
    if (effectiveStreamRequest) return dispatchPolicyFromRequest(effectiveStreamRequest);
    return resolveStreamDispatchPolicy(null, projectMetadata);
  }, [effectiveStreamRequest, projectMetadata]);
  const activeDispatchLabel = AMHS_STREAM_DISPATCH_LABELS[activeDispatchPolicy];
  // Redux writes land a render later, so the override is dropped once the
  // regenerated request catches up with it.
  useEffect(() => {
    if (!reviewedStreamRequest || !streamRequest) return;
    if (
      fingerprintAmhsStreamRequest(reviewedStreamRequest) ===
      fingerprintAmhsStreamRequest(streamRequest)
    ) {
      setReviewedStreamRequest(null);
    }
  }, [reviewedStreamRequest, streamRequest]);
  const streamFingerprint = useMemo(
    () => (effectiveStreamRequest ? fingerprintAmhsStreamRequest(effectiveStreamRequest) : null),
    [effectiveStreamRequest]
  );
  const liveStream = useAmhsLiveStream({
    fabId,
    floorPlanId,
    runRequest: effectiveStreamRequest,
    requestFingerprint: streamFingerprint,
  });
  const liveConnecting = liveStream.starting || simulation.live.connectionStatus === 'connecting';
  const liveConnected =
    liveConnecting ||
    (simulation.source === 'live' &&
      ['running', 'reconnecting', 'stopping'].includes(simulation.live.connectionStatus));
  const viewedLiveReport = simulation.live.liveReport ?? simulation.lastLiveReport;

  const persistReviewedProperties = (request: AmhsStreamRunRequest, reviewedFoupCount: number) => {
    setLiveSimSpeed(request.liveStream.simSpeed);
    setFoupValue(reviewedFoupCount);
    const reviewedJunctionWaitSec = request.trackNetwork.trackNetwork.junctions[0]?.waitSec;
    dispatch(
      patchProjectMetadata({
        liveStream: request.liveStream,
        handling: request.handling,
        collisionAvoidance: {
          headwaySec: request.collisionAvoidance.headwaySec,
          enforceNodeCapacity: request.collisionAvoidance.enforceNodeCapacity,
        },
        dispatchPolicy: request.ohtAssignment.dispatchPolicy ?? 'nearest',
        ohtAssignment: {
          dispatchPolicy: request.ohtAssignment.dispatchPolicy ?? 'nearest',
          preferPreferredOht: request.ohtAssignment.preferPreferredOht,
          allowIntrabayEmptyTravel: request.ohtAssignment.allowIntrabayEmptyTravel,
          ...(request.ohtAssignment.dispatchPolicy === 'ortools'
            ? {
                useOrtools: request.ohtAssignment.useOrtools ?? true,
                ortoolsTimeLimitS: request.ohtAssignment.ortoolsTimeLimitS,
                intelligentSharedOht: request.ohtAssignment.intelligentSharedOht,
                sharedOhtMinGainSec: request.ohtAssignment.sharedOhtMinGainSec,
                intelligentDedicatedAssist: request.ohtAssignment.intelligentDedicatedAssist,
                dedicatedJobsFirst: request.ohtAssignment.dedicatedJobsFirst,
              }
            : {}),
        },
        ...(typeof reviewedJunctionWaitSec === 'number' && reviewedJunctionWaitSec >= 0
          ? { junctionWaitSec: reviewedJunctionWaitSec }
          : {}),
      })
    );

    const processFlow = readProcessFlow(projectMetadata);
    const reviewedToolsById = new Map(request.tools.tools.map((tool) => [tool.toolId, tool]));
    let processFlowChanged = false;
    const processFlowSteps = processFlow.steps.map((step) => {
      const toolProcessTimes = { ...step.toolProcessTimes };
      let stepChanged = false;
      Object.keys(toolProcessTimes).forEach((toolId) => {
        const tool = reviewedToolsById.get(toolId);
        if (!tool) return;
        if (toolProcessTimes[toolId] === tool.processWaitSec) return;
        toolProcessTimes[toolId] = tool.processWaitSec;
        stepChanged = true;
      });
      if (!stepChanged) return step;
      processFlowChanged = true;
      return {
        ...step,
        toolProcessTimes,
      };
    });

    request.tools.tools.forEach((tool) => {
      const instance = allEquipmentInstances[tool.toolId];
      if (instance) {
        dispatch(
          updateInstance({
            id: tool.toolId,
            changes: {
              name: tool.name,
              equipmentType: normalizeEquipmentProcessType(tool.toolType),
              processWaitSec: tool.processWaitSec,
              foupCapacity: tool.foupCapacity,
            },
          })
        );
        return;
      }
      const element = elements[tool.toolId];
      if (!element) return;
      dispatch(
        updateElement({
          id: tool.toolId,
          updates: {
            metadata: { ...element.metadata, name: tool.name },
            customProperties: {
              ...(element.customProperties ?? {}),
              processType: tool.toolType,
              processWaitSec: tool.processWaitSec,
              foupCapacity: tool.foupCapacity,
            },
          },
        })
      );
    });

    request.stockers.stockers.forEach((stocker) => {
      const instance = allEquipmentInstances[stocker.stockerId];
      const processType = stocker.processType
        ? normalizeEquipmentProcessType(stocker.processType)
        : null;
      if (instance?.objectType === 'stocker') {
        dispatch(
          updateInstance({
            id: stocker.stockerId,
            changes: {
              name: stocker.name,
              foupCapacity: stocker.foupCapacity,
              processWaitSec: stocker.processWaitSec,
              processTypes: processType ? [processType] : [],
              requireInventoryBeforePick: stocker.requireInventoryBeforePick !== false,
              ...(processType ? { equipmentType: processType } : {}),
            },
          })
        );
        dispatch(
          setOutsideStockerExclusive(
            { kind: 'equipment', id: stocker.stockerId },
            stocker.outsideStocker
          )
        );
        return;
      }
      const element = elements[stocker.stockerId];
      if (!element || element.type !== 'stocker') return;
      dispatch(
        updateElement({
          id: stocker.stockerId,
          updates: {
            metadata: { ...element.metadata, name: stocker.name },
            customProperties: {
              ...(element.customProperties ?? {}),
              foupCapacity: stocker.foupCapacity,
              processWaitSec: stocker.processWaitSec,
              processTypes: processType ? [processType] : [],
              processType,
              requireInventoryBeforePick: stocker.requireInventoryBeforePick !== false,
            },
          },
        })
      );
      dispatch(
        setOutsideStockerExclusive(
          { kind: 'layout', id: stocker.stockerId },
          stocker.outsideStocker
        )
      );
    });

    request.ohts.ohts.forEach((oht) => {
      const element = elements[oht.ohtId];
      if (!element || element.type !== 'oht-vehicle') return;
      dispatch(
        updateElement({
          id: oht.ohtId,
          updates: {
            metadata: { ...element.metadata, name: oht.name },
            customProperties: {
              ...(element.customProperties ?? {}),
              ohtMode: oht.ohtMode ?? 'dedicated',
              ohtRole: oht.ohtRole ?? 'group',
              toolType: oht.toolType ?? null,
              cruiseSpeedOverride: oht.cruiseSpeed,
              dedicatedFrom: null,
              dedicatedTo: null,
            },
          },
        })
      );
    });

    if (processFlowChanged) {
      dispatch(
        patchProjectMetadata({
          processFlow: {
            ...processFlow,
            steps: processFlowSteps,
          },
        })
      );
    }
  };

  const readiness = useMemo(
    () =>
      collectAmhsRunReadiness({
        elements,
        equipmentInstances,
        equipmentTemplates,
        floorPlanId,
        floorPlanName,
        trackNodeAnnotations,
        projectMetadata: simulationProjectMetadata,
        includeTransportFlow,
      }),
    [
      elements,
      equipmentInstances,
      equipmentTemplates,
      floorPlanId,
      floorPlanName,
      trackNodeAnnotations,
      simulationProjectMetadata,
      includeTransportFlow,
    ]
  );
  const readinessIssues = readiness.issues;

  /* Temporarily hidden with Run (legacy) button
  const handleRunSimulation = async () => {
    setIsRunningRequest(true);
    setRunError(null);
    // Close any open OHT popup; user must click an OHT to open details again.
    dispatch(clearSelection());
    try {
      const request = buildAmhsRunRequest({
        elements,
        equipmentInstances,
        equipmentTemplates,
        floorPlanId,
        floorPlanName,
        trackNodeAnnotations,
        projectMetadata: simulationProjectMetadata,
        includeTransportFlow,
      });
      const response = await runAmhsSimulation(request);
      const browserKinetic = buildBrowserKineticFromFinalSimulation(
        response,
        Object.values(elements)
      );
      dispatch(
        loadSimulationResponse({
          browserKinetic,
          transportRequests: extractTransportRequests(response),
          assignments: response.finalOptimization.assignments,
          amhsReports: {
            summary: response.summary,
            report: response.report,
            simulationCompareReport: response.simulationCompareReport,
            features: response.features,
            responseIncludes: response.responseIncludes,
            // Authoritative “after” KPIs — same source as playback.
            finalSimulationKpis: response.finalSimulation?.kpis,
            utilizationOptimization:
              response.utilizationOptimization ??
              (response.summary?.utilizationOptimization as Record<string, unknown> | undefined) ??
              (response.finalOptimization as { utilizationOptimization?: Record<string, unknown> })
                ?.utilizationOptimization,
          },
        })
      );
      dispatch(setShowSimulationTrackNodes(true));
      dispatch(startSimulation());
    } catch (error) {
      if (error instanceof AmhsRunApiError) {
        setRunError(error.formatted);
      } else {
        setRunError(formatAmhsRunError(error));
      }
    } finally {
      setIsRunningRequest(false);
    }
  };
  */

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        title="Expand simulation panel"
        aria-label="Expand simulation panel"
        className="absolute top-3 right-3 z-30 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[11px] font-semibold tracking-wider text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-800"
      >
        SIM
        <ChevronLeftIcon />
      </button>
    );
  }

  const status =
    simulation.source === 'live' && simulation.live.connectionStatus !== 'idle'
      ? simulation.live.connectionStatus[0].toUpperCase() +
        simulation.live.connectionStatus.slice(1)
      : simulation.finished
        ? 'Finished'
        : simulation.paused
          ? 'Paused'
          : simulation.playing
            ? 'Playing'
            : 'Stopped';

  return (
    <aside
      className="absolute bottom-3 right-3 top-3 z-30 flex w-[22.5rem] min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      aria-label="Simulation panel"
    >
      <div className="shrink-0 border-b border-gray-200 px-3 pt-3 pb-2 text-sm">
        <div className="flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setExpanded(false)}
              title="Collapse simulation panel"
              aria-label="Collapse simulation panel"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
            >
              <ChevronRightIcon />
            </button>
            <div className="flex items-center gap-1">
              <h2 className="text-base font-semibold text-gray-900">Simulation</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setProcessFlowModalOpen(true)}
            title="Define the process flow, dedicated stockers and tool process times"
            className="shrink-0 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            Configure
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3 text-sm scrollbar-thin">
        <div className="space-y-3">
          <section
            className="space-y-3 overflow-hidden rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
            aria-label="Simulation details"
          >
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-[11px] font-semibold tracking-[0.14em] text-slate-800">
                SIMULATION DETAILS
              </h3>
              <div className="flex items-center gap-1.5">
                <span
                  className="rounded-full bg-sky-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-sky-800"
                  title="Active OHT dispatch policy for the next or current live stream"
                >
                  {activeDispatchLabel}
                </span>
                <span className="rounded-full bg-black px-2 py-0.5 text-[10px] font-medium text-white">
                  {status}
                </span>
              </div>
            </div>

            {(runError || liveStream.actionError) && (
              <div
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-900"
              >
                Something went wrong,{' '}
                <button
                  type="button"
                  onClick={() => setErrorDetailsModalOpen(true)}
                  className="font-semibold text-blue-700 underline decoration-blue-400 underline-offset-2 hover:text-blue-900"
                >
                  click here to view details
                </button>
                .
              </div>
            )}

            {liveStream.layoutChanged && (
              <div
                role="status"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900"
              >
                <p className="font-semibold">
                  Layout changed since this stream was{' '}
                  {liveStream.layoutMatchKnown ? 'started' : 'discovered'}.
                </p>
                <p className="mt-0.5">
                  Resume would use the server&apos;s older layout. Stop and restart to apply the
                  current loops, tools and OHTs.
                </p>
              </div>
            )}

            {liveStream.discovery?.active &&
              !liveStream.layoutChanged &&
              !liveStream.layoutMatchKnown &&
              !liveConnected && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-900">
                  This job may have been started elsewhere. Resume uses its server-owned layout;
                  local edits are not uploaded while attaching.
                </div>
              )}

            <div>
              <button
                type="button"
                onClick={() => setReviewConfigModalOpen(true)}
                disabled={!effectiveStreamRequest}
                className="text-xs font-semibold text-blue-700 underline decoration-blue-400 underline-offset-2 hover:text-blue-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
              >
                Review config before starting simulation
              </button>
              {reviewedStreamRequest && (
                <div className="mt-2 flex items-center justify-between gap-2 text-[10px] text-amber-800">
                  <span className="font-medium">Temporary testing override will be sent.</span>
                  <button
                    type="button"
                    onClick={() => setReviewedStreamRequest(null)}
                    className="shrink-0 font-semibold underline underline-offset-2 hover:text-amber-950"
                  >
                    Clear override
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  if (liveStream.layoutChanged) {
                    void liveStream.restart();
                  } else if (liveStream.discovery?.active) {
                    liveStream.resume();
                  } else {
                    liveStream.start();
                  }
                }}
                disabled={
                  liveStream.discovering ||
                  liveStream.starting ||
                  liveConnected ||
                  (!liveStream.discovery?.active &&
                    !liveStream.sessionActive &&
                    !effectiveStreamRequest)
                }
                title={
                  liveStream.layoutChanged
                    ? 'Stop the existing API job and start with this layout'
                    : liveConnecting
                      ? 'Starting the live stream'
                      : liveStream.discovery?.active || liveStream.sessionActive
                        ? 'Attach to the API-owned live job'
                        : `Start a live stream with ${activeDispatchLabel} dispatch`
                }
                className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {liveStream.discovering && !liveStream.starting
                  ? 'Discovering…'
                  : liveConnecting
                    ? 'Starting…'
                    : liveStream.layoutChanged
                      ? '↻ Stop & restart'
                      : liveStream.sessionActive || liveStream.discovery?.active
                        ? '▶ Resume live'
                        : '▶ Start live'}
              </button>

              <button
                type="button"
                onClick={() => void liveStream.stop()}
                disabled={
                  (!liveStream.discovery?.active &&
                    !liveStream.sessionActive &&
                    !simulation.live.streamId) ||
                  liveStream.stopPending
                }
                title={
                  liveStream.stopPending
                    ? 'Waiting for the API to confirm the stop'
                    : 'Stop the API-owned live job'
                }
                className="rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {liveStream.stopPending ? 'Stopping…' : '■ Stop live'}
              </button>

              {liveStream.discovery?.active && !liveConnected && !liveStream.layoutChanged && (
                <div className="col-span-2 flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-[10px] text-slate-600">
                  <span>
                    A job for this layout is already running on the server
                    {typeof liveStream.discovery.simTimeS === 'number'
                      ? ` at ${formatSeconds(liveStream.discovery.simTimeS)} sim time`
                      : ''}
                    . Resume attaches to it.
                  </span>
                  <button
                    type="button"
                    onClick={() => liveStream.startFresh()}
                    disabled={liveStream.starting || !effectiveStreamRequest}
                    title="Stop the server job and start a new one from the current layout"
                    className="shrink-0 font-semibold text-blue-700 underline decoration-blue-400 underline-offset-2 hover:text-blue-900 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Start fresh
                  </button>
                </div>
              )}

              {simulation.source === 'legacy' && simulation.running && (
                <>
                  {simulation.paused ? (
                    <button
                      type="button"
                      onClick={() => dispatch(resumePlayback())}
                      className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700"
                    >
                      ▶ Resume playback
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => dispatch(pausePlayback())}
                      disabled={simulation.finished}
                      className="rounded bg-amber-500 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      ⏸ Pause playback
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => dispatch(stopSimulation())}
                    className="rounded bg-slate-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-700"
                  >
                    ■ Stop playback
                  </button>
                </>
              )}

              {/* Temporarily hidden: Run (legacy) button
              <button
                type="button"
                onClick={handleRunSimulation}
                disabled={isRunningRequest || liveStream.locallyAttached}
                title="Run the unchanged one-shot /amhs/run workflow"
                className="col-span-2 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isRunningRequest ? 'Running legacy API…' : 'Run (legacy)'}
              </button>
              */}

              {/* Temporarily hidden: Restart playback button
              {simulation.source === 'legacy' && (
                <button
                  type="button"
                  onClick={() => {
                    dispatch(clearSelection());
                    dispatch(restartSimulation());
                  }}
                  disabled={!simulation.browserKinetic}
                  className="col-span-2 rounded bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  ↻ Restart playback
                </button>
              )}
              */}
            </div>

            {simulation.source === 'live' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-[11px] text-emerald-950">
                <div className="flex justify-between gap-3">
                  <span>Server time</span>
                  <span className="font-mono tabular-nums">
                    {formatSeconds(simulation.simulationTime)}
                  </span>
                </div>
                <div className="mt-1 flex justify-between gap-3">
                  <span>API speed</span>
                  <span className="font-mono">{simulation.live.simSpeed}×</span>
                </div>
                <div className="mt-1 flex justify-between gap-3">
                  <span>Dispatch</span>
                  <span className="font-medium">{activeDispatchLabel}</span>
                </div>
                <div className="mt-1 flex justify-between gap-3">
                  <span>Connection</span>
                  <span className="font-medium">{simulation.live.connectionStatus}</span>
                </div>
                <div className="mt-2 border-t border-emerald-200 pt-2">
                  <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-800">
                    Dispatch performance ({activeDispatchLabel})
                  </p>
                  <LiveDispatchReportPanel
                    report={viewedLiveReport}
                    dispatchLabel={activeDispatchLabel}
                    compact
                  />
                </div>
                {/* Temporarily hide live OHT junction dwell / wait messages.
                <div className="mt-2 space-y-2">
                  {Object.values(simulation.live.collisions).map((collision) => (
                    <div
                      key={collision.ohtId}
                      className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1 text-red-900"
                    >
                      {collision.ohtId}{' '}
                      {collision.type === 'junction_wait'
                        ? collision.reason === 'junctionDwell'
                          ? 'junction dwell'
                          : 'junction waiting'
                        : 'waiting'}
                      {collision.blockingOhtId ? ` for ${collision.blockingOhtId}` : ''}
                      {collision.reason ? ` · ${collision.reason}` : ''}
                      {collision.type === 'junction_wait' ? ` · ${collision.waitSec}s` : ''}
                    </div>
                  ))}
                </div>
                */}
              </div>
            )}

            {!liveConnected && simulation.lastLiveReport && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[11px] text-slate-800">
                <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  Last live dispatch report
                </p>
                <div className="mt-1.5">
                  <LiveDispatchReportPanel
                    report={simulation.lastLiveReport}
                    dispatchLabel={activeDispatchLabel}
                    compact
                  />
                </div>
              </div>
            )}

            {simulation.source === 'legacy' &&
              simulation.browserKinetic &&
              !simulation.replayPlan && (
                <div
                  role="status"
                  className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-900"
                >
                  Non-deterministic legacy playback: this response has no versioned{' '}
                  <span className="font-mono">visualizationEvents</span> timeline.
                </div>
              )}

            {/* Temporarily hidden: simulation time slider, rewind/forward/skip-wait controls
            {simulation.source === 'legacy' && (
              <div>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="font-medium text-gray-600">Simulation time</span>
                  <span className="tabular-nums text-gray-500">
                    {formatSeconds(simulation.simulationTime)} /{' '}
                    {formatSeconds(simulation.durationS)}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.max(simulation.durationS, 0.001)}
                  step={0.1}
                  value={Math.min(simulation.simulationTime, simulation.durationS)}
                  disabled={!simulation.browserKinetic || simulation.durationS <= 0}
                  onChange={(event) => dispatch(seekSimulationTime(Number(event.target.value)))}
                  className="h-1.5 w-full cursor-pointer accent-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Simulation timeline"
                />
                <div className="mt-2 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    title={`Rewind ${timelineStepSeconds(simulation.durationS)}s`}
                    aria-label="Rewind simulation"
                    disabled={!simulation.browserKinetic || simulation.durationS <= 0}
                    onClick={() =>
                      dispatch(nudgeSimulationTime(-timelineStepSeconds(simulation.durationS)))
                    }
                    className="flex-1 rounded bg-slate-100 px-2 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    ⏪ Rewind
                  </button>
                  <button
                    type="button"
                    title={`Forward ${timelineStepSeconds(simulation.durationS)}s`}
                    aria-label="Forward simulation"
                    disabled={!simulation.browserKinetic || simulation.durationS <= 0}
                    onClick={() =>
                      dispatch(nudgeSimulationTime(timelineStepSeconds(simulation.durationS)))
                    }
                    className="flex-1 rounded bg-slate-100 px-2 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Forward ⏩
                  </button>
                  <button
                    type="button"
                    title="Skip long collision-avoidance waits to the next OHT travel (useful with foupReleaseMode=gap)"
                    aria-label="Skip to next travel"
                    disabled={!simulation.browserKinetic || simulation.durationS <= 0}
                    onClick={() => {
                      const next = nextTravelMotionTime(apiLegs, simulation.simulationTime);
                      if (next == null) return;
                      dispatch(seekSimulationTime(Math.min(simulation.durationS, next + 0.05)));
                    }}
                    className="flex-1 rounded bg-amber-50 px-2 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Skip wait ▶
                  </button>
                </div>
                <p className="mt-1 text-[10px] leading-snug text-slate-400">
                  With <span className="font-mono">foupReleaseMode=gap</span>, FOUPs overlap and CA
                  waits can last minutes at 1× — chip shows Wait, or use Skip wait.
                </p>
              </div>
            )}
            */}

            {/* Temporarily hidden: phase legend (Travel / Deadhead / Wait / Idle chips)
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {(
                [
                  ['travel', 'Travel'],
                  ['deadhead', 'Deadhead'],
                  ['wait', 'Wait'],
                  ['idle', 'Idle'],
                ] as const
              ).map(([chip, text]) => {
                const c = phaseChipColors(chip);
                return (
                  <span
                    key={chip}
                    className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
                    style={{ backgroundColor: c.bg, color: c.fg }}
                  >
                    {text}
                  </span>
                );
              })}
              <span className="text-[10px] text-slate-400">· active window = bright stroke</span>
            </div>
            <p className="text-[10px] text-slate-500">
              Travel = loaded delivery · Deadhead = empty reposition · path uses{' '}
              <code className="rounded bg-slate-100 px-0.5">segmentWindows</code>
            </p>
            */}

            <div className="flex items-center justify-between pt-1">
              <span className="text-xs font-medium text-gray-600">Label visibility</span>
              <LabelsTogglePopup />
            </div>

            {simulation.source === 'legacy' &&
              !simulation.browserKinetic &&
              !runError &&
              readinessIssues.length === 0 && (
                <p className="text-[11px] text-amber-700">
                  Run the simulation to load the backend movement timeline.
                </p>
              )}
          </section>

          <div
            className="flex gap-1 rounded-lg bg-slate-100 p-0.5"
            role="tablist"
            aria-label="Simulation panel tabs"
          >
            {DETAIL_TABS.map((item) => {
              const selected = detailTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setDetailTab(item.id)}
                  className={`flex-1 rounded-md px-1.5 py-1.5 text-[11px] font-semibold transition-colors ${
                    selected
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {detailTab === 'overview' && (
            <div className="space-y-3" role="tabpanel" aria-label="Overview">
              <TransportRequestsPanel />
            </div>
          )}
          {detailTab === 'oht' && (
            <div role="tabpanel" aria-label="OHT">
              <SelectedOhtPanel />
            </div>
          )}
          {detailTab === 'stocker' && (
            <div role="tabpanel" aria-label="Stocker">
              <LiveInventoryPanel />
            </div>
          )}
          {detailTab === 'tool' && (
            <div role="tabpanel" aria-label="Tool">
              <ToolInventoryPanel />
            </div>
          )}
        </div>
      </div>

      {processFlowModalOpen && (
        <ProcessFlowConfigModal onClose={() => setProcessFlowModalOpen(false)} />
      )}
      {reviewConfigModalOpen && streamRequest && effectiveStreamRequest && (
        <ReviewSimulationConfigModal
          request={effectiveStreamRequest}
          generatedRequest={streamRequest}
          foupCount={foupValue}
          hasOverride={Boolean(reviewedStreamRequest)}
          onPersist={persistReviewedProperties}
          onApply={(request, isTestingOverride) => {
            // JSON edits stay in memory for the next live start. Property saves
            // write to their real sources and must not create or replace that override.
            if (isTestingOverride) setReviewedStreamRequest(request);
          }}
          onClearOverride={() => setReviewedStreamRequest(null)}
          onClose={() => setReviewConfigModalOpen(false)}
        />
      )}
      {errorDetailsModalOpen && (runError || liveStream.actionError) && (
        <SimulationErrorModal
          runError={runError}
          liveError={liveStream.actionError}
          onClose={() => setErrorDetailsModalOpen(false)}
        />
      )}
    </aside>
  );
};

type SimulationErrorModalProps = {
  runError: AmhsFormattedRunError | null;
  liveError: string | null;
  onClose: () => void;
};

const SimulationErrorModal: React.FC<SimulationErrorModalProps> = ({
  runError,
  liveError,
  onClose,
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-90 flex items-center justify-center bg-gray-950/45 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="simulation-error-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id="simulation-error-title" className="text-base font-semibold text-slate-900">
              Error details
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Review the simulation errors below and update the layout or request as needed.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close error details"
            className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800"
          >
            Close
          </button>
        </header>

        <div className="px-5 py-4">
          <div className="h-72 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] scrollbar-thin">
            {liveError && (
              <div className="rounded-md border border-red-200 bg-white px-3 py-2 text-red-900">
                <p className="font-semibold">Live simulation request failed</p>
                <p className="mt-1 wrap-break-word text-red-800">{liveError}</p>
              </div>
            )}

            {runError && (
              <>
                {runError.issues.length > 0 ? (
                  <>
                    <p className="font-semibold text-red-900">{runError.headline}</p>
                    <ValidationIssueAccordion
                      severity="error"
                      issues={runError.issues.filter((issue) => issue.severity === 'error')}
                      defaultOpen
                    />
                    <ValidationIssueAccordion
                      severity="warning"
                      issues={runError.issues.filter((issue) => issue.severity === 'warning')}
                      defaultOpen
                    />
                    <ValidationIssueAccordion
                      severity="info"
                      issues={runError.issues.filter((issue) => issue.severity === 'info')}
                      defaultOpen
                    />
                  </>
                ) : (
                  <div className="rounded-md border border-red-200 bg-white px-3 py-2 text-red-900">
                    <p className="font-semibold">{runError.headline}</p>
                    {runError.detail && (
                      <p className="mt-1 wrap-break-word text-red-800">{runError.detail}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

type ValidationIssueAccordionProps = {
  severity: 'error' | 'warning' | 'info';
  issues: AmhsFormattedValidationIssue[];
  defaultOpen?: boolean;
};

const VALIDATION_SECTION_STYLE = {
  error: {
    label: 'Errors',
    border: 'border-red-200',
    background: 'bg-red-50',
    header: 'text-red-900',
    count: 'bg-red-200 text-red-900',
    item: 'border-red-100 bg-white/75',
    code: 'text-red-700',
    title: 'text-red-950',
    body: 'text-red-800/90',
  },
  warning: {
    label: 'Warnings',
    border: 'border-amber-200',
    background: 'bg-amber-50',
    header: 'text-amber-900',
    count: 'bg-amber-200 text-amber-900',
    item: 'border-amber-100 bg-white/75',
    code: 'text-amber-700',
    title: 'text-amber-950',
    body: 'text-amber-800/90',
  },
  info: {
    label: 'Info',
    border: 'border-blue-200',
    background: 'bg-blue-50',
    header: 'text-blue-900',
    count: 'bg-blue-200 text-blue-900',
    item: 'border-blue-100 bg-white/75',
    code: 'text-blue-700',
    title: 'text-blue-950',
    body: 'text-blue-800/90',
  },
} as const;

const ValidationIssueAccordion: React.FC<ValidationIssueAccordionProps> = ({
  severity,
  issues,
  defaultOpen = false,
}) => {
  const style = VALIDATION_SECTION_STYLE[severity];

  if (issues.length === 0) return null;

  return (
    <details
      open={defaultOpen}
      className={`group overflow-hidden rounded-md border ${style.border} ${style.background}`}
    >
      <summary
        className={`flex cursor-pointer list-none items-center gap-2 px-2.5 py-2 font-semibold ${style.header}`}
      >
        <span className="inline-block text-[10px] transition-transform group-open:rotate-90">
          ▶
        </span>
        <span>{style.label}</span>
        <span className={`ml-auto rounded-full px-1.5 py-0.5 text-[10px] ${style.count}`}>
          {issues.length}
        </span>
      </summary>

      <ul className="space-y-1.5 px-2 py-2">
        {issues.map((issue, index) => (
          <li
            key={`${issue.code}-${issue.path ?? ''}-${index}`}
            className={`rounded border px-2 py-1.5 ${style.item}`}
          >
            <span className={`font-mono text-[10px] ${style.code}`}>{issue.code}</span>
            <p className={`mt-1 font-medium ${style.title}`}>{issue.title}</p>
            <p className={`mt-0.5 ${style.body}`}>{issue.meaning}</p>
            {issue.path && <p className={`mt-0.5 text-[10px] ${style.body}`}>Path: {issue.path}</p>}
            {issue.suggestion && (
              <p className={`mt-0.5 text-[10px] ${style.body}`}>Suggestion: {issue.suggestion}</p>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
};

const ChevronRightIcon: React.FC = () => (
  <svg
    className="h-3.5 w-3.5"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
  >
    <path d="M5 3.5 9 7 5 10.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg
    className="h-3.5 w-3.5"
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
  >
    <path d="M9 3.5 5 7 9 10.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default SimulationPanel;
